/**
 * @file        src/pages/hotel/BookingList.jsx
 * @description B2C Hotel Booking List over hotel_bookings. Legacy parity:
 *              Success / Pending summary cards (click to filter), Export (CSV),
 *              Clear Filter, and the reference columns — ID, Booking Date,
 *              B Status, City, Check In, Check Out, Customer, Fare, Action.
 *
 *              Check In / Check Out are not columns on hotel_bookings; the API
 *              attaches them from the stored booking_details JSON.
 * @author      Kinetika Labs
 * @created     2026-07-02
 * @changelog
 *   - 2026-10-09 Claude — A new ?open= while mounted opens that drawer (global search).
 *   - 2026-10-06 Claude — Status chip prints cells.statusLabel (Booked → Success,
 *                          unsettled → Pending), the word the storefront prints.
 *   - 2026-09-23 Claude — Document links carry the screen they were opened from
 *                          (lib/docOrigin), so a corporate booking's invoice /
 *                          e-ticket keeps the Corporate group open in the
 *                          sidebar and its Back button returns to THIS list.
 *   - 2026-07-02 Claude — Initial (B2C Hotel Management).
 *   - 2026-07-12 Claude — Rebuilt on the shared ReadOnlyListPage with the legacy
 *                          columns, status stat-cards, CSV export and a Details
 *                          modal (was a bare Ref/Hotel/Coupon table).
 *   - 2026-07-27 Claude — Accept title/subtitle/defaultAudience/lockAudience
 *                          props so CorporateBookingList.jsx can reuse this
 *                          component fixed to audience=b2b instead of forking it.
 *   - 2026-08-03 Claude — Brand filter is registry-driven (BrandSelect), not the
 *                          hardcoded Neon/Beyond pair.
 *   - 2026-08-04 Claude — CR D3/D4: dense rows + sticky header (ReadOnlyListPage
 *                          `dense`), right-aligned Fare, the numeric ID folded
 *                          into the Booking Date cell (one less column), and
 *                          the row buttons collapsed into one RowMenu (Export
 *                          stays a standalone toolbar action). Added a
 *                          booking-date range, a travel/stay-date range and a
 *                          passenger/guest search, all reflected in the URL
 *                          via useSearchParams (flight-list parity) so a
 *                          filtered view is a shareable link.
 *   - 2026-08-04 Claude — Agent retirement (CR E8): removed the conditional
 *                          Commission column (b2b_commission) from the
 *                          Corporate Booking List — new bookings no longer
 *                          record commission; historical values are still in
 *                          the DB but no longer surfaced here.
 *   - 2026-08-04 Claude — CR D2: added a "Share" (email icon) row action
 *                          opening ShareEmailModal (Voucher/Invoice/Both, real
 *                          PDF attachment, logged).
 *   - 2026-08-10 Claude — Row actions follow the docAccess() rule: Invoice /
 *                          Voucher / Share only exist for the statuses the
 *                          client approved (pending & cancellation-pending →
 *                          none, confirmed → all, cancelled → invoice only).
 *   - 2026-09-15 Claude — The audience PICKER is gone and `audience` is no longer a
 *                          URL filter. This B2C-labeled screen offered a "Customer
 *                          type" dropdown whose options included "B2B / Corporate"
 *                          and "All (B2C + B2B)", so corporate bookings could be
 *                          pulled into the B2C screen (and "All" sent
 *                          `audience=all`, which the API's b2c|b2b enum rejects
 *                          outright). B2C and Corporate are SEPARATE screens, not
 *                          two filter values of one screen: the audience is now
 *                          fixed by the component's `audience` prop, is always sent
 *                          to the API, and cannot be overridden from the query
 *                          string — so a shared link cannot cross the boundary
 *                          either. `defaultAudience`/`lockAudience` are replaced by
 *                          the single `audience` prop.
 *                          The CSV export takes the same fixed audience.
 *   - 2026-08-10 Claude — Defaults to audience=b2c instead of the unfiltered
 *                          "All" view — this B2C-labeled screen was showing B2B
 *                          bookings mixed in by default even though a dedicated
 *                          Corporate Booking List already exists. "All" is now
 *                          an explicit dropdown choice (value 'all', not '') so
 *                          it round-trips through the URL distinctly from "no
 *                          filter chosen yet".
 *   - 2026-09-15 Claude — Guest filter capped at 120 chars, matching the route.
 *                          (A parallel "All (B2C + B2B)" fix was dropped at merge:
 *                          the picker it patched no longer exists.)
 */
import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReadOnlyListPage from '../flight/ReadOnlyListPage';
import Chip from '../../components/Chip';
import Icon from '../../components/Icon';
import BrandSelect from '../../components/BrandSelect';
import DatePicker from '../../components/DatePicker';
import RowMenu from '../../components/RowMenu';
import ShareEmailModal from '../../components/ShareEmailModal';
import { get } from '../../api/client';
import { toast } from '../../lib/toast';
import { docPath } from '../../lib/docOrigin';
import { money, day, dateTime, statusTone, statusLabel, customerCell, downloadCsv, docAccess } from './cells';
import BookingDetailsDrawer from './BookingDetailsDrawer';
import UpdateBookingModal from './UpdateBookingModal';
import CancelBookingModal from './CancelBookingModal';

const CARDS = [
  { key: 'success', label: 'Success', tone: 'card-ok' },
  { key: 'pending', label: 'Pending', tone: 'card-pend' },
  { key: 'failed', label: 'Failed', tone: 'card-rej' },
  { key: 'cancellation_pending', label: 'Cancellation Pending', tone: 'card-pend' },
  { key: 'cancelled', label: 'Cancelled', tone: 'card-rej' },
];

// CSV shape = what the table shows, so an export matches the screen.
const CSV_COLS = [
  { label: 'ID', key: 'id' },
  { label: 'Booking Ref', key: 'booking_reference' },
  { label: 'Booking Date', get: (r) => dateTime(r.created_at) },
  { label: 'B Status', key: 'booking_status' },
  { label: 'Hotel', key: 'hotel_name' },
  { label: 'City', key: 'city_name' },
  { label: 'Check In', get: (r) => day(r.check_in) },
  { label: 'Check Out', get: (r) => day(r.check_out) },
  { label: 'Customer', get: (r) => r.customer_name || (r.user_id ? '' : 'Guest') },
  { label: 'Email', key: 'customer_email' },
  { label: 'Fare', key: 'total_paid' },
];

// Filters kept in the URL (D4) — everything a shared link should reproduce.
// page/pageSize and the Ref/Hotel/City search box stay ReadOnlyListPage-local.
// The booking screens are audience-SEPARATED, not audience-filtered: /hotel/bookings
// is B2C-only and the Corporate Booking List is B2B-only. `audience` is therefore
// NOT a URL filter — it is fixed by the screen, cannot be overridden from the query
// string, and has no picker.
const URL_KEYS = ['status', 'brand', 'bookingFrom', 'bookingTo', 'travelFrom', 'travelTo', 'passenger'];

export default function BookingList({
  title = 'Hotel Booking List',
  subtitle = 'B2C Hotel · hotel bookings',
  // Which audience this SCREEN is. Not a default the admin can change — see the
  // note above URL_KEYS.
  audience = 'b2c',
} = {}) {
  const [updateRow, setUpdateRow] = useState(null); // the row being edited (DB-only)
  const [cancelRow, setCancelRow] = useState(null); // the row being cancelled
  const [shareRow, setShareRow] = useState(null); // the row being shared by email
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  const [params, setParams] = useSearchParams();

  // ?open=<booking id> lifts this booking's details drawer immediately — the
  // link the Action Centre sends for a refund, so "work the refund" lands on the
  // refund pipeline instead of on a list with the reference still to find.
  // Seeded ONCE: after that the drawer is ordinary state, and closing it clears
  // the param so a refresh does not reopen what was just dismissed. `open` is
  // deliberately absent from URL_KEYS — it selects a row, it does not filter the
  // list, so it must not reach the API or light up the "filters active" chip.
  const [detailsId, setDetailsId] = useState(() => {
    const v = Number(params.get('open'));
    return Number.isInteger(v) && v > 0 ? v : null;
  });
  // A new ?open= while this screen is already mounted (the global search) opens
  // that booking too — the seed above only runs on the first render.
  const openParam = params.get('open');
  useEffect(() => {
    const v = Number(openParam);
    if (Number.isInteger(v) && v > 0) setDetailsId(v);
  }, [openParam]);
  const closeDetails = () => {
    setDetailsId(null);
    if (params.get('open')) setFilters({ open: '' });
  };
  const status = params.get('status') || null;

  const brand = params.get('brand') || '';
  const bookingFrom = params.get('bookingFrom') || '';
  const bookingTo = params.get('bookingTo') || '';
  const travelFrom = params.get('travelFrom') || '';
  const travelTo = params.get('travelTo') || '';
  const passenger = params.get('passenger') || '';

  /** Merge one or more filter changes into the URL in a single navigation. */
  function setFilters(patch) {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v); else next.delete(k);
    }
    setParams(next, { replace: true });
  }

  // The stat cards belong to THIS screen's audience. Counting every booking made
  // the B2C screen advertise corporate volume (and vice-versa), and since a card
  // is also a status filter, "Booked: 120" then listed a different number of rows.
  const { data: sum } = useQuery({
    queryKey: ['hotel-bookings', 'summary', audience],
    queryFn: () => get('/hotel/bookings/summary', { params: { audience } }),
  });
  const counts = sum?.data || {};

  async function onExport() {
    setExporting(true);
    try {
      // Same filters as the screen (D4) — an export should match what's visible,
      // and must never cross the screen's audience boundary either.
      const exportParams = { audience };
      for (const k of URL_KEYS) {
        const v = params.get(k) || '';
        if (v) exportParams[k] = v;
      }
      const res = await get('/hotel/bookings/export', { params: exportParams });
      const rows = res?.data || [];
      if (!rows.length) { toast.err('Nothing to export', 'No bookings match this filter.'); return; }
      downloadCsv(`hotel-bookings-${new Date().toISOString().slice(0, 10)}.csv`, CSV_COLS, rows);
      // The API caps the export so a huge table can't take the console down —
      // say so rather than silently handing over a truncated file.
      if (res?.meta?.capped) toast.err('Export truncated', 'Only the most recent 5000 bookings were exported.');
      else toast.ok('Exported', `${rows.length} booking${rows.length > 1 ? 's' : ''} downloaded.`);
    } catch (e) {
      toast.err('Export failed', e.response?.data?.error?.message || 'Could not export bookings.');
    } finally {
      setExporting(false);
    }
  }

  const anyFilterActive = URL_KEYS.some((k) => params.get(k));

  const toolbar = (
    <>
      <div className="stat-row">
        {CARDS.map((c) => (
          <button
            key={c.key}
            className={`stat-card ${c.tone}${status === c.key ? ' active' : ''}`}
            onClick={() => setFilters({ status: status === c.key ? '' : c.key })}
            title={`Filter ${c.label}`}
          >
            <span className="stat-num">{counts[c.key] ?? '—'}</span>
            <span className="stat-label">{c.label}</span>
          </button>
        ))}
        <button className="btn success" onClick={onExport} disabled={exporting}>
          <Icon name="download" /> {exporting ? 'Exporting…' : 'Export'}
        </button>
        {anyFilterActive ? (
          <button className="btn danger" onClick={() => setParams(new URLSearchParams(), { replace: true })}>
            <Icon name="x" /> Clear Filter
          </button>
        ) : null}
        <div className="field" style={{ minWidth: 150, flex: '0 0 auto' }}>
          <BrandSelect value={brand} onChange={(v) => setFilters({ brand: v })} withAll placeholder="Brand" />
        </div>
      </div>
      <div className="filter-row">
        <div className="field">
          <label>Booking date from</label>
          <DatePicker value={bookingFrom} onChange={(v) => setFilters({ bookingFrom: v })} />
        </div>
        <span className="date-sep">–</span>
        <div className="field">
          <label>Booking date to</label>
          <DatePicker value={bookingTo} onChange={(v) => setFilters({ bookingTo: v })} min={bookingFrom} />
        </div>
        <div className="field">
          <label>Stay date from</label>
          <DatePicker value={travelFrom} onChange={(v) => setFilters({ travelFrom: v })} />
        </div>
        <span className="date-sep">–</span>
        <div className="field">
          <label>Stay date to</label>
          <DatePicker value={travelTo} onChange={(v) => setFilters({ travelTo: v })} min={travelFrom} />
        </div>
        <div className="field grow">
          <label>Guest name / contact</label>
          <input
            value={passenger}
            maxLength={120}
            onChange={(e) => setFilters({ passenger: e.target.value })}
            placeholder="Search by guest name, email…"
          />
        </div>
      </div>
    </>
  );

  // The screen's audience always goes to the API; the rest are the admin's filters.
  const extraParams = { audience };
  for (const k of URL_KEYS) {
    const v = params.get(k) || '';
    if (v) extraParams[k] = v;
  }

  const rowActions = (row) => {
    // Invoice / Voucher / Share follow the client-approved status rule — see
    // docAccess(). Details (the cart view) and Update are admin tools and stay.
    // Cancel is confirmed-only: the booking backend rejects cancelling anything
    // else, so don't dangle a button that would only come back "cannot be
    // cancelled".
    const docs = docAccess(row.booking_status);
    return (
      <RowMenu
        label="Actions"
        items={[
          { key: 'details', label: 'Details', icon: 'search', onClick: () => setDetailsId(row.id) },
          { key: 'update', label: 'Update', icon: 'edit', onClick: () => setUpdateRow(row) },
          { key: 'invoice', label: 'Invoice', icon: 'download', hidden: !docs.invoice, onClick: () => navigate(docPath(`/hotel/bookings/${row.id}/invoice`, audience)) },
          { key: 'voucher', label: 'Voucher', icon: 'page', hidden: !docs.voucher, onClick: () => navigate(docPath(`/hotel/bookings/${row.id}/voucher`, audience)) },
          { key: 'share', label: 'Share', icon: 'mail', hidden: !docs.share, onClick: () => setShareRow(row) },
          { key: 'cancel', label: 'Cancel', icon: 'x', danger: true, hidden: !docs.confirmed, onClick: () => setCancelRow(row) },
        ]}
      />
    );
  };

  return (
    <>
      <ReadOnlyListPage
        title={title}
        subtitle={subtitle}
        endpoint="/hotel/bookings"
        queryRoot="hotel-bookings"
        searchLabel="Booking Ref / Hotel / City"
        actionsLabel="Action"
        rowActions={rowActions}
        toolbar={toolbar}
        extraParams={Object.keys(extraParams).length ? extraParams : undefined}
        showIndex={false}
        dense
        columns={[
          {
            key: 'created_at',
            label: 'Booking',
            render: (v, r) => (
              <div>
                <div>{dateTime(v)}</div>
                <div className="muted" style={{ fontSize: '.8em' }} title={`Trace ID ${r.id}`}>#{r.id}</div>
              </div>
            ),
          },
          { key: 'booking_status', label: 'Status', render: (v) => (v ? <Chip tone={statusTone(v)}>{statusLabel(v)}</Chip> : '—') },
          { key: 'city_name', label: 'City', render: (v, r) => v || r.hotel_name || <span className="muted">—</span> },
          { key: 'check_in', label: 'Check In', render: day },
          { key: 'check_out', label: 'Check Out', render: day },
          { key: 'customer_name', label: 'Customer', render: customerCell },
          { key: 'total_paid', label: 'Fare', align: 'right', render: (v) => <b>{money(v)}</b> },
        ]}
      />
      {detailsId ? (
        <BookingDetailsDrawer
          bookingId={detailsId}
          audience={audience}
          onClose={closeDetails}
          onUpdate={(b) => { closeDetails(); setUpdateRow(b); }}
          onCancel={(b) => { closeDetails(); setCancelRow(b); }}
        />
      ) : null}
      {updateRow ? <UpdateBookingModal booking={updateRow} onClose={() => setUpdateRow(null)} /> : null}
      {cancelRow ? <CancelBookingModal booking={cancelRow} onClose={() => setCancelRow(null)} /> : null}
      {shareRow ? <ShareEmailModal kind="hotel" booking={shareRow} onClose={() => setShareRow(null)} /> : null}
    </>
  );
}
